import { ShoppingCart, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { Modal, ModalFooter } from './Modal';
import type { ChecklistItem } from '../../types';

interface PurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: ChecklistItem;
  onConfirm: (quantity: number) => void;
  isPending: boolean;
}

export function PurchaseModal({
  isOpen,
  onClose,
  item,
  onConfirm,
  isPending,
}: PurchaseModalProps) {
  const { t } = useLanguage();

  // Calculate remaining
  const remaining = (item.quantityNeeded || 1) - (item.quantityPurchased || 0);

  // Use remaining as initial value - parent uses key prop to reset on item change
  const [quantity, setQuantity] = useState(remaining);

  const handleConfirm = () => {
    onConfirm(quantity);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('checklists.recordPurchase', 'Record Purchase')}
      icon={ShoppingCart}
      iconColor="green"
      size="sm"
    >
      <div className="space-y-6">
        {/* Item info */}
        <div className="text-center p-4 bg-gray-50 rounded-xl">
          <p className="font-medium text-gray-900">{item.name}</p>
          <p className="text-sm text-gray-600 mt-1">
            {remaining} {t('checklists.remaining', 'remaining')}
          </p>
        </div>

        {/* Quantity input */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('checklists.howManyPurchasing', 'How many are you purchasing?')}
          </label>
          <input
            type="number"
            min={1}
            max={remaining}
            value={quantity}
            onChange={e => setQuantity(Math.max(1, Math.min(remaining, parseInt(e.target.value) || 1)))}
            className="w-full px-4 py-3 text-center text-xl font-semibold border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-shadow"
          />
        </div>

        {/* Quick select buttons */}
        {remaining > 1 && (
          <div className="flex gap-2 justify-center">
            {[1, Math.ceil(remaining / 2), remaining].filter((v, i, a) => a.indexOf(v) === i).map(num => (
              <button
                key={num}
                onClick={() => setQuantity(num)}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  quantity === num
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {num}
              </button>
            ))}
          </div>
        )}

        <ModalFooter>
          <button
            onClick={onClose}
            className="px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
          >
            {t('common.cancel', 'Cancel')}
          </button>
          <button
            onClick={handleConfirm}
            disabled={isPending}
            className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-green-600 rounded-xl hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
          >
            {isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            {t('checklists.confirm', 'Confirm')}
          </button>
        </ModalFooter>
      </div>
    </Modal>
  );
}
